/**
 * Splits a duration into an object with days, hours, minutes, seconds and milliseconds
 * @param duration0 Duration in seconds or milliseconds
 * @param delimiter Optional delimiter to return only specific units
 * @param isMilliseconds Whether the input duration is in milliseconds
 */
export function splitDuration(duration0, delimiter = '', isMilliseconds = false) {
  if (duration0 <= 0) return {};

  // Convert everything to seconds for consistent calculations
  let totalSeconds = isMilliseconds ? Math.floor(duration0 / 1000) : duration0;
  const milliseconds = isMilliseconds ? duration0 % 1000 : 0;

  // Return only requested units based on delimiter
  if (delimiter === 'minutes') {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return { minutes, seconds };
  }
  if (delimiter === 'seconds') {
    return { seconds: totalSeconds, milliseconds };
  }

  // Calculate each unit for full breakdown
  const days = Math.floor(totalSeconds / 86400);
  totalSeconds %= 86400;
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Return all units
  return {
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
  };
}

/**
 * Converts a 'yyyy-mm-dd hh:mm:ss' (from mySql) to a ISO string
 */
export function toISOString(val) {
  return `${val?.replace(' ', 'T')}Z`;
}

/**
 * Converts a JS date in to a "YYYY-mm-dd HH:MM:SS" string (mySql style)
 * @param date
 * @param keepTimezone - keep the timezone in the final string
 * @returns {string}
 */
export function toMySqlFormat(date, keepTimezone = false) {
  let localDate = date?.$isDayjsObject ? date.toDate() : date;
  if (!date) localDate = new Date();
  if (!keepTimezone) {
    // The final date string will be in UTC
    const datePart = localDate.toISOString()
      .split('T')[0];
    const timePart = localDate.toISOString()
      .split('T')[1].split('.')[0];

    return `${datePart} ${timePart}`;
  }

  // The final date string will be in the local timezone
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0'); // Les mois sont indexés à partir de 0
  const day = String(localDate.getDate()).padStart(2, '0');
  const hours = String(localDate.getHours()).padStart(2, '0');
  const minutes = String(localDate.getMinutes()).padStart(2, '0');
  const seconds = String(localDate.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Convert seconds in object with duration and duration type string
 */
export function convertSecondsInDuration(duration0, factor = 1) {
  let localFactor = factor;
  if (!(localFactor >= 1 && localFactor <= 10)) localFactor = 1;
  const value = duration0;
  let toReturn = { value: 0, i18nKey: 'common.datetime.minute_tc' };

  if (value >= 60) {
    const minutes = Math.floor(value / 60);
    toReturn = { i18nKey: 'common.datetime.minute_tc', value: minutes };

    if (value >= 3600) {
      const hours = Math.floor(value / 3600);
      if (hours >= localFactor) toReturn = { i18nKey: 'common.datetime.hour_tc', value: hours };

      if (value >= 86400) {
        const days = Math.floor(value / 86400);
        if (days >= localFactor) toReturn = { i18nKey: 'common.datetime.day_tc', value: days };

        if (value >= 604800) {
          const weeks = Math.floor(value / 604800);
          if (weeks >= localFactor) toReturn = { i18nKey: 'common.datetime.week_tc', value: weeks };

          if (value >= 2628000) {
            const months = Math.floor(value / 2628000);
            if (months >= localFactor) toReturn = { i18nKey: 'common.datetime.month_tc', value: months };

            if (value >= 31540000) {
              const years = Math.floor(value / 31540000);
              if (years >= localFactor) toReturn = { i18nKey: 'common.datetime.year_tc', value: years };
            }
          }
        }
      }
    }
  }

  return toReturn;
}

/**
 * Format a duration in to a nice string (hh:mm)
 * @param {number} seconds
 * @returns {{hours: (*|string), minutes: (*|string)}}
 */
export function durationToHhmm(seconds) {
  if (typeof seconds !== 'number') {
    throw new Error('Argument must be a number');
  }

  const separator = ':';
  // eslint-disable-next-line no-use-before-define
  const hm = secondsToHms(seconds, false);
  const [hours, minutes] = hm.split(separator);

  return { hours, minutes };
}

/**
 * Convert seconds into hh:mm:ss.uuu format.
 *
 * @returns String
 * @param nbOfSeconds
 * @param showSeconds
 * @param showMilliSeconds
 * @param includeZeroMilliseconds
 * @param showHours
 */
export function secondsToHms(
  nbOfSeconds,
  showSeconds = false,
  showMilliSeconds = false,
  includeZeroMilliseconds = false,
  showHours = true,
  hourDigits = 2,
) {
  let seconds = Number(nbOfSeconds);
  let u = null;
  if (showMilliSeconds) {
    u = Math.floor(seconds % 1000);
    seconds /= 1000;
  }
  let h = Math.floor(seconds / 3600);
  // h = h < 10 ? `0${h}` : h;
  let m = Math.floor(seconds / 60);
  m %= 60;
  // m = m < 10 ? `0${m}` : m;
  const s = Math.floor(seconds % 3600 % 60);
  // s = s < 10 ? `0${s}` : s;

  if (showMilliSeconds && (u > 0 || includeZeroMilliseconds)) {
    if (showHours) {
      return `${(`${h}`).padStart(hourDigits, '0')}:${(`${m}`).padStart(2, '0')}:${(`${s}`).padStart(2, '0')}.${(`${u}`).padStart(3, '0')}`;
    }
    return `${(`${s}`).padStart(2, '0')}.${(`${u}`).padStart(3, '0')}`;
  }

  if (!showHours && showSeconds) {
    return `${(`${m}`).padStart(2, '0')}:${(`${s}`).padStart(2, '0')}`;
  }

  if (showSeconds && showHours) {
    return `${(`${h}`).padStart(hourDigits, '0')}:${(`${m}`).padStart(2, '0')}:${(`${s}`).padStart(2, '0')}`;
  }

  if (s >= 60) {
    m += 1;
  }

  if (m >= 60) {
    m = 0;
    h += 1;
  }

  return `${(`${h}`).padStart(hourDigits, '0')}:${(`${m}`).padStart(2, '0')}`;
}

/**
 * See if a date is included in a period
 * Use date string "YYYY-mm-dd HH:MM:SS"
 * @param periodStart
 * @param periodEnd
 * @param date
 * @returns {boolean}
 */
export function dateInRange(periodStart, periodEnd, date) {
  return (date >= periodStart && date <= periodEnd);
}

/**
 * Checks that a value is an ISO 8601 datetime with a timezone offset (or "Z"), e.g.
 * "2026-09-15T22:00:00+00:00", and that it represents a real calendar date/time
 * (rejects out-of-range values like "2026-13-45T99:99:99+00:00").
 * @param value
 * @returns {boolean}
 */
export function isIsoDateTime(value) {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2}|Z)$/.test(value)
    && !Number.isNaN(Date.parse(value));
}

export function hmsToSeconds(hhmmss) {
  try {
    const [h, m, s] = hhmmss.padEnd(8, ':00').split(':');
    if (h && m && s) return +h * 3600 + +m * 60 + +s;
  } catch (e) {
    throw new Error('Input must be a string with a valid hour with the format : hh:mm:ss or hh:mm');
  }
  throw new Error('Input must be a string with a valid hour with the format : hh:mm:ss or hh:mm');
}

export function stringToMiliseconds(hhmmssuuu) {
  try {
    const [h, m, s, u] = hhmmssuuu.split(/[:.]/);
    if (h && m && s && u) return `${+h * 3600 + +m * 60 + +s}${u}`;
  } catch (e) {
    throw new Error('Input must be a string with a valid hour with the format : hh:mm:ss.uuu');
  }
  throw new Error('Input must be a string with a valid hour with the format : hh:mm:ss.uuu');
}

/**
 * Takes a UTC date string and format it to a local one
 * @param utcString
 * @param dayjs
 * @returns {*}
 */
export function toLocalFormat(utcString, dayjs) {
  return dayjs.utc(utcString).local().format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Convert an object days.js to a string date
 * @returns {string}
 */
export function objectDayToString(date) {
  const hours = date.hours() === 0
    ? date.hours() : date.days() * 24 + date.hours();
  return `
    ${(`${hours}`).padStart(2, '0')}:${(`${date.minutes()}`).padStart(2, '0')}:${(`${date.seconds()}`).padStart(2, '0')}
  `;
}

/**
 * Convert all data to an integer.
 */
export function convertToMilliseconds(hours, minutes, seconds, milliseconds) {
  return parseInt(hours, 10) * 3600000
    + parseInt(minutes, 10) * 60000
    + parseInt(seconds, 10) * 1000
    + parseInt(milliseconds, 10);
}
